/**
 * Motion primitives.
 *
 * framer-motion is used for ONE job in this application: an element that slides
 * between DOM positions. That is the tab pill and the sidebar's current-page
 * marker, and nothing else. Entrances, panel swaps and press feedback are CSS
 * keyframes in styles.css - they cost nothing at runtime, run off the main
 * thread, and need no JavaScript on the critical path.
 *
 * `LazyMotion` keeps the tree-shakeable `m.*` components: importing `motion.*`
 * instead pulls the whole feature set into the entry bundle whether a screen
 * uses it or not.
 *
 * The feature set is `domMax`, not `domAnimation`. Layout ids need layout
 * projection, which only domMax carries; under domAnimation a `layoutId` element
 * does not animate at all - it disappears from the old position and reappears at
 * the new one, which is exactly what both pills used to do.
 *
 * `MotionConfig reducedMotion="user"` makes every animation here honour the
 * operating system's reduce-motion setting automatically, which matters:
 * movement triggers vestibular symptoms for some people, so this is an
 * accessibility control rather than a preference.
 */
import { AnimatePresence, LazyMotion, MotionConfig, domMax, m } from 'motion/react';
import { useEffect, type ReactNode } from 'react';

/**
 * The spring for anything sliding between fixed slots.
 *
 * Crisp, with effectively no overshoot: damping is stiffness/12.5, and the rule
 * of thumb is that stiffness/13 lands just short of overshooting. Lower that
 * ratio for playfulness, raise it for authority.
 *
 * Deliberately no `duration`. Passing one turns a spring into a tween and gives
 * up the interruption behaviour that makes rapid clicking feel right.
 */
export const SPRING_SLIDE = { type: 'spring', stiffness: 400, damping: 32 } as const;

export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={domMax} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}

/**
 * How one item of a list arrives, leaves, and moves.
 *
 * These lists change under the reader rather than only on a page load: a row is
 * approved and the filter drops it, a tile is created, a scope tab swaps the
 * whole set. Without this the change is a jump cut - the item is simply
 * somewhere else on the next frame, and nothing says whether it was added,
 * removed, or moved.
 *
 * ARRIVING IS A FADE, not a rise. The card these lists sit in runs its own
 * entrance, so an item that also translated was carried up by its ancestor and
 * moved eight pixels of its own: it appeared below where it belonged and crawled
 * into place, which reads as the list snapping rather than loading. The card
 * carries the movement; the item carries the fade, and is at its final position
 * from the first frame it can be seen.
 *
 * Leaving keeps a small scale, because there the movement is the message - it
 * says the row was removed rather than repainted - and nothing is arriving
 * underneath it to be dragged along.
 *
 * `MotionConfig reducedMotion="user"` above turns all of it off for anybody
 * whose system asks for that.
 */
export const LIST_ITEM = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, scale: 0.97, transition: { duration: 0.14, ease: 'easeIn' } },
  transition: { duration: 0.18, ease: 'easeOut' },
} as const;

/**
 * How an item MOVES when its neighbours arrive or leave.
 *
 * Its own transition, and the reason this block exists. Moves used to inherit
 * the entrance's tween - including its per-item delay - and a delayed layout
 * animation is not held at the old position while it waits: measured frame by
 * frame, a tile snapped to its new slot, slid back towards the old one, then
 * slid forward again (837 → 1106 → 937 → 1106 px), and the tiles after it did
 * the same one delay-step later. That staircase is the jagged look.
 *
 * A spring with no delay: every tile starts moving on the same frame, and a
 * second re-render mid-move (the list revalidates, then the change feed
 * revalidates it again) retargets the spring instead of restarting it.
 */
export const LIST_MOVE = { type: 'spring', stiffness: 520, damping: 42, mass: 0.9 } as const;

/**
 * The entrance delayed by the item's place in the list - the fade only.
 *
 * A list that fades in as one block reads as a screen redraw; the same list
 * arriving in sequence reads as content landing. Capped at a quarter of a
 * second, because the twentieth row must not wait for the nineteenth. The
 * delay is on `opacity` alone: `layout` has its own undelayed spring above,
 * and the exit carries its own undelayed transition, so a removed item leaves
 * at once rather than after its index's wait.
 */
export const listItem = (index: number) => ({
  ...LIST_ITEM,
  transition: {
    ...LIST_ITEM.transition,
    delay: Math.min(index * 0.03, 0.24),
    layout: LIST_MOVE,
  },
});

/**
 * What sits BELOW a list glides when the list grows or shrinks.
 *
 * The items themselves already arrive and leave smoothly (listItem, and the
 * row glide in Table). What still jumped was everything after them: a new row
 * of tiles, a new table row, an empty-state note going away - the list's
 * height changed on one frame, so the next card, and the rest of the card
 * under the list, teleported. Measured on the JMC clinic page: the content
 * under the CTA grid moved 31px in a single frame when the first CTA landed.
 *
 * One observer for the whole screen instead of one per list: there are some
 * seventy lists, and a fix that each of them has to opt into is a fix most of
 * them will not have.
 *
 * FLIP, timed by ResizeObserver - which runs after layout and before paint, so
 * a block that moved is started back at its old place before a frame shows it
 * anywhere else. Tracked: every card and every direct child of a card, which is
 * the grain the screens are built at. Positions are compared ON SCREEN and
 * re-recorded on every scroll, so neither the reader scrolling nor the
 * browser's scroll anchoring counts as a move. A child's move is taken
 * relative to its card's, so a card that slides does not also slide its
 * contents a second time. Framer-driven items (`data-motion`) and anything
 * inside an open editor are left alone.
 */
export function useBlockGlide(root: { current: HTMLElement | null }) {
  useEffect(() => {
    const host = root.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    const seen = new Map<HTMLElement, number>();
    let width = window.innerWidth;

    const blocks = () =>
      Array.from(host.querySelectorAll<HTMLElement>('.card, .card > *')).filter(
        (el) => el.dataset.motion === undefined && !el.closest('[contenteditable="true"]'),
      );
    // The layout position, without whatever glide is running on it now.
    const topOf = (el: HTMLElement) => {
      const transform = getComputedStyle(el).transform;
      const shift = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m42;
      return el.getBoundingClientRect().top - shift;
    };
    const record = () => {
      for (const el of blocks()) seen.set(el, topOf(el));
    };

    const resized = new ResizeObserver(() => {
      const now = blocks();
      const moved = new Map<HTMLElement, number>();
      const animate = !still.matches && window.innerWidth === width;
      width = window.innerWidth;
      for (const el of now) {
        const top = topOf(el);
        const before = seen.get(el);
        seen.set(el, top);
        if (!animate || before === undefined || el.offsetParent === null) continue;
        const parent = el.parentElement?.closest<HTMLElement>('.card') ?? null;
        const delta = before - top - (parent ? (moved.get(parent) ?? 0) : 0);
        moved.set(el, before - top);
        if (Math.abs(delta) < 2) continue;
        el.animate([{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0)' }], {
          duration: 260,
          easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        });
      }
      for (const el of now) resized.observe(el);
    });
    resized.observe(host);
    for (const el of blocks()) resized.observe(el);
    record();

    // New blocks are watched from the render they arrive in; departed ones are forgotten.
    const added = new MutationObserver(() => {
      for (const el of blocks()) resized.observe(el);
      for (const el of seen.keys()) if (!el.isConnected) seen.delete(el);
    });
    added.observe(host, { childList: true, subtree: true });

    let pending = 0;
    const onScroll = () => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        record();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      resized.disconnect();
      added.disconnect();
      cancelAnimationFrame(pending);
      window.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [root]);
}

export { AnimatePresence, m };
